import React from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';
import Loader from '../../../../common/Loader';

/**
 * MoveReplaceConfirmModal Component
 * Modal popup for confirming move operation
 * Reuses structure from CopyReplaceConfirmModal
 */
const MoveReplaceConfirmModal = ({
  moveDocument,
  moveNode,
  moveTargetDocument,
  movePosition,
  comment,
  onCommentChange,
  onClose,
  onSave,
  isLoading,
  moveCreateCopy,
  originalData
}) => {
  const dispatch = useDispatch();

  const handleSave = () => {
    if (!comment || !comment.trim()) {
      dispatch(showSnackbar({ message: 'Comment is mandatory.', type: 'error' }));
      return;
    }
    onSave();
  };

  const targetDocument = moveTargetDocument || moveDocument;
  
  // Get document type from moveDocument (already fetched in NodeAppBar)
  const getDocumentType = () => {
    return targetDocument?.type || 'Document';
  };

  // Generate confirmation message based on operation type and position
  const getConfirmationMessage = () => {
    if (!moveNode || !movePosition) {
      return moveCreateCopy ? 'Confirm Copy Operation' : 'Confirm Move Operation';
    }

    const operation = moveCreateCopy ? 'Copy' : 'Move';
    const position = movePosition.position;

    // Handle step operations - early return to prevent affecting section logic
    if (moveNode.nodeType === 'step') {
      const nodeTitle = moveNode.nodeTitle || 'Step';
      if (position === 'into') {
        // For step INTO section: "Confirm Move/Copy of Step 1 Into Section 2"
        let targetSectionTitle = '';
        
        // Get target section info from movePosition
        if (movePosition.targetType === 'section' && movePosition.targetId && originalData) {
          const sortedSections = [...originalData].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          
          const targetSection = sortedSections.find(
            section => section.tree_node_id === movePosition.targetId || 
                       section.section_id_ref === movePosition.targetId
          );
          
          if (targetSection) {
            // Always use section number, not section name
            const targetIndex = sortedSections.findIndex(
              s => s.tree_node_id === targetSection.tree_node_id || 
                   s.section_id_ref === targetSection.section_id_ref
            );
            if (targetIndex !== -1) {
              targetSectionTitle = `Section ${targetIndex + 1}`;
            }
          } else if (movePosition.targetIndex !== undefined && movePosition.targetIndex !== null) {
            // Fallback: use provided index
            targetSectionTitle = `Section ${movePosition.targetIndex + 1}`;
          }
        }
        
        if (targetSectionTitle) {
          return `Confirm ${operation} of ${nodeTitle} Into ${targetSectionTitle}`;
        } else {
          return `Confirm ${operation} of ${nodeTitle}`;
        }
      } else {
        // For step ABOVE/BELOW: "Confirm Move/Copy of Step 1 Above/Below Step 8"
        let targetStepTitle = '';
        
        // Use globalStepNumber from movePosition if available (set by MovePositionSelector)
        if (movePosition.globalStepNumber !== undefined && movePosition.globalStepNumber !== null) {
          targetStepTitle = `Step ${movePosition.globalStepNumber}`;
        } else if (originalData && movePosition.targetId) {
          // Fallback: Search through all sections and their steps to find target step
          for (const section of originalData) {
            const targetStep = section.steps?.find(
              step => step.tree_node_id === movePosition.targetId || 
                      step.step_id_ref === movePosition.targetId
            );
            
            if (targetStep) {
              // Calculate global step number for target step
              let globalStepNumber = 0;
              const sortedSections = [...originalData].sort((a, b) => {
                const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
                const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
                return orderA - orderB;
              });
              
              const sectionIndex = sortedSections.findIndex(
                s => s.tree_node_id === section.tree_node_id || 
                     s.section_id_ref === section.section_id_ref
              );
              
              // Count steps in previous sections
              for (let i = 0; i < sectionIndex; i++) {
                globalStepNumber += sortedSections[i].steps?.length || 0;
              }
              
              // Find target step index within its section
              const stepIndex = section.steps?.findIndex(
                step => step.tree_node_id === movePosition.targetId || 
                        step.step_id_ref === movePosition.targetId
              );
              
              if (stepIndex !== -1) {
                globalStepNumber += stepIndex + 1;
                targetStepTitle = `Step ${globalStepNumber}`;
              }
              break;
            }
          }
        }
        
        // Final fallback
        if (!targetStepTitle) {
          targetStepTitle = 'Target Step';
        }

        const positionText = position === 'above' ? 'Above' : 'Below';
        return `Confirm ${operation} of ${nodeTitle} ${positionText} ${targetStepTitle}`;
      }
    }

    // Handle substep operations
    if (moveNode.nodeType === 'substep') {
      const nodeTitle = moveNode.nodeTitle || 'Substep';
      if (position === 'into') {
        // For substep INTO step: "Confirm Move/Copy of Substep 1A Into Step 5"
        let targetStepTitle = '';
        
        // Check if targetType is step (substep moving into step)
        if (movePosition.targetType === 'step' && movePosition.targetId && originalData) {
          // Use globalStepNumber from movePosition if available
          if (movePosition.globalStepNumber !== undefined && movePosition.globalStepNumber !== null) {
            targetStepTitle = `Step ${movePosition.globalStepNumber}`;
          } else {
            // Fallback: Search through all sections and their steps to find target step
            for (const section of originalData) {
              const targetStep = section.steps?.find(
                step => step.tree_node_id === movePosition.targetId || 
                       step.step_id_ref === movePosition.targetId
              );
              
              if (targetStep) {
                // Calculate global step number for target step
                let globalStepNumber = 0;
                const sortedSections = [...originalData].sort((a, b) => {
                  const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
                  const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
                  return orderA - orderB;
                });
                
                const sectionIndex = sortedSections.findIndex(
                  s => s.tree_node_id === section.tree_node_id || 
                       s.section_id_ref === section.section_id_ref
                );
                
                // Count steps in previous sections
                for (let i = 0; i < sectionIndex; i++) {
                  globalStepNumber += sortedSections[i].steps?.length || 0;
                }
                
                // Find target step index within its section
                const stepIndex = section.steps?.findIndex(
                  step => step.tree_node_id === movePosition.targetId || 
                         step.step_id_ref === movePosition.targetId
                );
                
                if (stepIndex !== -1) {
                  globalStepNumber += stepIndex + 1;
                  targetStepTitle = `Step ${globalStepNumber}`;
                }
                break;
              }
            }
          }
        }
        
        if (targetStepTitle) {
          return `Confirm ${operation} of ${nodeTitle} Into ${targetStepTitle}`;
        } else {
          // Fallback if target step not found
          return `Confirm ${operation} of ${nodeTitle}`;
        }
      } else {
        // For substep ABOVE/BELOW: "Confirm Move/Copy of Substep 1A Above/Below Substep 8B"
        let targetSubstepTitle = '';
        
        // Use substep label from movePosition if available
        if (movePosition.substepLetter !== undefined && movePosition.substepLetter !== null && movePosition.globalStepNumber !== undefined && movePosition.globalStepNumber !== null) {
          targetSubstepTitle = `Substep ${movePosition.globalStepNumber}${movePosition.substepLetter}`;
        } else if (originalData && movePosition.targetId) {
          // Fallback: Search through all sections, steps, and their substeps to find target substep
          for (const section of originalData) {
            for (const step of section.steps || []) {
              const targetSubstep = step.substeps?.find(
                substep => substep.tree_node_id === movePosition.targetId || 
                           substep.substep_id_ref === movePosition.targetId
              );
              
              if (targetSubstep) {
                // Calculate global step number and substep letter for target substep
                const sortedSections = [...originalData].sort((a, b) => {
                  const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
                  const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
                  return orderA - orderB;
                });
                
                const sectionIndex = sortedSections.findIndex(
                  s => s.tree_node_id === section.tree_node_id || 
                       s.section_id_ref === section.section_id_ref
                );
                
                // Count steps in previous sections
                let globalStepNumber = 0;
                for (let i = 0; i < sectionIndex; i++) {
                  globalStepNumber += sortedSections[i].steps?.length || 0;
                }
                
                // Find target step index within its section
                const stepIndex = section.steps?.findIndex(
                  step => step.tree_node_id === step.tree_node_id || 
                         step.step_id_ref === step.step_id_ref
                );
                
                if (stepIndex !== -1) {
                  globalStepNumber += stepIndex + 1;
                  
                  // Find target substep index within its step
                  const substepIndex = step.substeps?.findIndex(
                    substep => substep.tree_node_id === movePosition.targetId || 
                               substep.substep_id_ref === movePosition.targetId
                  );
                  
                  if (substepIndex !== -1) {
                    const substepLetter = String.fromCharCode(65 + substepIndex); // A, B, C...
                    targetSubstepTitle = `Substep ${globalStepNumber}${substepLetter}`;
                  }
                }
                break;
              }
            }
            if (targetSubstepTitle) break;
          }
        }
        
        // Final fallback
        if (!targetSubstepTitle) {
          targetSubstepTitle = 'Target Substep';
        }

        const positionText = position === 'above' ? 'Above' : 'Below';
        return `Confirm ${operation} of ${nodeTitle} ${positionText} ${targetSubstepTitle}`;
      }
    }
    
    // Handle child node operations (attachment, video, rich-text, canvas, form)
    if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(moveNode.nodeType)) {
      const nodeTitle = moveNode.nodeTitle || moveNode.nodeType.charAt(0).toUpperCase() + moveNode.nodeType.slice(1);
      
      if (position === 'into') {
        // For child node INTO step/substep: "Confirm Move/Copy of Attachment 2 Into Step 1" or "Into Substep 1A"
        let targetTitle = '';
        
        // Check if targetType is step or substep
        if (movePosition.targetType === 'step' && movePosition.targetId && originalData) {
          // Use globalStepNumber from movePosition if available
          if (movePosition.globalStepNumber !== undefined && movePosition.globalStepNumber !== null) {
            targetTitle = `Step ${movePosition.globalStepNumber}`;
          } else {
            // Fallback: Search through all sections and their steps to find target step
            for (const section of originalData) {
              const targetStep = section.steps?.find(
                step => step.tree_node_id === movePosition.targetId || 
                       step.step_id_ref === movePosition.targetId
              );
              
              if (targetStep) {
                // Calculate global step number for target step
                let globalStepNumber = 0;
                const sortedSections = [...originalData].sort((a, b) => {
                  const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
                  const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
                  return orderA - orderB;
                });
                
                const sectionIndex = sortedSections.findIndex(
                  s => s.tree_node_id === section.tree_node_id || 
                       s.section_id_ref === section.section_id_ref
                );
                
                // Count steps in previous sections
                for (let i = 0; i < sectionIndex; i++) {
                  globalStepNumber += sortedSections[i].steps?.length || 0;
                }
                
                // Find target step index within its section
                const stepIndex = section.steps?.findIndex(
                  step => step.tree_node_id === movePosition.targetId || 
                         step.step_id_ref === movePosition.targetId
                );
                
                if (stepIndex !== -1) {
                  globalStepNumber += stepIndex + 1;
                  targetTitle = `Step ${globalStepNumber}`;
                }
                break;
              }
            }
          }
        } else if (movePosition.targetType === 'substep' && movePosition.targetId && originalData) {
          // For substep targets, find the substep and calculate its label
          for (const section of originalData) {
            for (const step of section.steps || []) {
              const targetSubstep = step.substeps?.find(
                substep => substep.tree_node_id === movePosition.targetId || 
                           substep.substep_id_ref === movePosition.targetId
              );
              
              if (targetSubstep) {
                // Calculate global step number and substep letter
                let globalStepNumber = 0;
                const sortedSections = [...originalData].sort((a, b) => {
                  const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
                  const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
                  return orderA - orderB;
                });
                
                const sectionIndex = sortedSections.findIndex(
                  s => s.tree_node_id === section.tree_node_id || 
                       s.section_id_ref === section.section_id_ref
                );
                
                // Count steps in previous sections
                for (let i = 0; i < sectionIndex; i++) {
                  globalStepNumber += sortedSections[i].steps?.length || 0;
                }
                
                // Find target step index within its section
                const stepIndex = section.steps?.findIndex(
                  stepItem => stepItem.step_id_ref === step.step_id_ref || 
                             stepItem.tree_node_id === step.tree_node_id
                );
                
                if (stepIndex !== -1) {
                  globalStepNumber += stepIndex + 1;
                  
                  // Find target substep index within its step
                  const substepIndex = step.substeps?.findIndex(
                    substep => substep.tree_node_id === movePosition.targetId || 
                               substep.substep_id_ref === movePosition.targetId
                  );
                  
                  if (substepIndex !== -1) {
                    const substepLetter = String.fromCharCode(65 + substepIndex); // A, B, C...
                    targetTitle = `Substep ${globalStepNumber}${substepLetter}`;
                  }
                }
                break;
              }
            }
            if (targetTitle) break;
          }
        }
        
        if (targetTitle) {
          return `Confirm ${operation} of ${nodeTitle} Into ${targetTitle}`;
        } else {
          // Fallback if target not found
          return `Confirm ${operation} of ${nodeTitle}`;
        }
      } else {
        // For child node ABOVE/BELOW another child: "Confirm Move/Copy of Attachment 1 Above/Below Canvas 1"
        let targetChildTitle = '';
        
        // Try to find target child node in original data and calculate its global number
        if (originalData && movePosition.targetId) {
          // Helper function to check if a child node matches the targetId
          // Must check all possible ID fields since target could be any child node type
          const matchesTargetId = (child) => {
            // Check tree_node_id first (most common)
            if (child.tree_node_id === movePosition.targetId) return true;
            
            // Check all possible detail ID fields for different child node types
            const detailIdFields = [
              'canvas_tree_node_id',
              'attachment_tree_node_id',
              'video_tree_node_id',
              'rich_text_node_id',
              'form_tree_node_id',
            ];
            
            for (const field of detailIdFields) {
              if (child.detail?.[field] === movePosition.targetId) return true;
            }
            
            return false;
          };
          
          // Sort sections by sibling_order (same as MovePositionSelector)
          const sortedSections = [...originalData].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          
          // First, find the target child to determine its type
          let targetChild = null;
          for (const section of sortedSections) {
            for (const step of section.steps || []) {
              targetChild = step.children?.find(matchesTargetId);
              if (targetChild) break;
              
              for (const substep of step.substeps || []) {
                targetChild = substep.children?.find(matchesTargetId);
                if (targetChild) break;
              }
              if (targetChild) break;
            }
            if (targetChild) break;
          }
          
          if (targetChild) {
            // Get node type (normalize canvas to canva for filtering, same as MovePositionSelector)
            const nodeType = targetChild.type === 'canvas' ? 'canva' : targetChild.type;
            let counter = 0;
            
            // Count all nodes of this type before the target across all sections, steps, and substeps
            for (let i = 0; i < sortedSections.length; i++) {
              const section = sortedSections[i];
              
              for (let j = 0; j < (section.steps?.length || 0); j++) {
                const step = section.steps[j];
                
                // Count step-level children of the same type
                const stepChildren = step.children?.filter((c) => c.type === nodeType) || [];
                
                // Check if this is our target child in a step
                const targetChildInStep = stepChildren.find(matchesTargetId);
                if (targetChildInStep) {
                  // Add children before this one in the step
                  const childIndex = stepChildren.findIndex(matchesTargetId);
                  counter += childIndex !== -1 ? childIndex : 0;
                  const globalNodeNumber = counter + 1;
                  
                  const childTypeLabel = targetChild.type === 'rich-text' ? 'Rich Text' : 
                                       targetChild.type === 'canva' ? 'Canvas' : 
                                       targetChild.type.charAt(0).toUpperCase() + targetChild.type.slice(1);
                  targetChildTitle = `${childTypeLabel} ${globalNodeNumber}`;
                  break;
                } else {
                  counter += stepChildren.length;
                }
                
                // Count substep-level children of the same type
                for (let k = 0; k < (step.substeps?.length || 0); k++) {
                  const substep = step.substeps[k];
                  const substepChildren = substep.children?.filter((c) => c.type === nodeType) || [];
                  
                  // Check if this is our target child in a substep
                  const targetChildInSubstep = substepChildren.find(matchesTargetId);
                  if (targetChildInSubstep) {
                    // Add children before this one in the substep
                    const childIndex = substepChildren.findIndex(matchesTargetId);
                    counter += childIndex !== -1 ? childIndex : 0;
                    const globalNodeNumber = counter + 1;
                    
                    const childTypeLabel = targetChild.type === 'rich-text' ? 'Rich Text' : 
                                         targetChild.type === 'canva' ? 'Canvas' : 
                                         targetChild.type.charAt(0).toUpperCase() + targetChild.type.slice(1);
                    targetChildTitle = `${childTypeLabel} ${globalNodeNumber}`;
                    break;
                  } else {
                    counter += substepChildren.length;
                  }
                }
                
                if (targetChildTitle) break;
              }
              if (targetChildTitle) break;
            }
          }
        }
        
        // Final fallback
        if (!targetChildTitle) {
          const childTypeLabel = moveNode.nodeType === 'rich-text' ? 'Rich Text' : 
                               moveNode.nodeType === 'canva' ? 'Canvas' : 
                               moveNode.nodeType.charAt(0).toUpperCase() + moveNode.nodeType.slice(1);
          targetChildTitle = `Target ${childTypeLabel}`;
        }
        
        const positionText = position === 'above' ? 'Above' : 'Below';
        return `Confirm ${operation} of ${nodeTitle} ${positionText} ${targetChildTitle}`;
      }
    }
    
    // Handle section operations (existing logic - unchanged)
    // This code path is only executed when moveNode.nodeType === 'section'
    if (moveNode.nodeType !== 'section') {
      // Fallback for unknown node types
      return moveCreateCopy ? 'Confirm Copy Operation' : 'Confirm Move Operation';
    }
    
    const sectionTitle = moveNode.nodeTitle || 'Section';
    
    if (position === 'into') {
      // For "into", show document type and part number
      const docType = getDocumentType();
      const partNumber = targetDocument.partNumber || '';
      return `Confirm ${operation} of ${sectionTitle} Into ${docType} ${partNumber}`;
    } else {
      // For "above" or "below", find the target section
      let targetSectionTitle = '';
      
      // Get source section ID to exclude it from target calculation
      const sourceSectionId = moveNode.nodeId || moveNode.sectionId;
      
      // Sort sections to match the order used in MovePositionSelector
      const sortedSections = originalData ? [...originalData].sort((a, b) => {
        const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
        const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
        return orderA - orderB;
      }) : [];
      
      // First, try to use targetIndex if available - but recalculate from sorted array for accuracy
      if (originalData && movePosition.targetId && movePosition.targetId !== sourceSectionId) {
        // Find target section from targetId - MUST exclude source section
        const targetSection = sortedSections.find(
          section => {
            const isMatch = (section.tree_node_id === movePosition.targetId || 
                           section.section_id_ref === movePosition.targetId);
            // CRITICAL: Exclude source section to prevent showing "Section 1 Below Section 1"
            const isNotSource = section.tree_node_id !== sourceSectionId && 
                               section.section_id_ref !== sourceSectionId;
            return isMatch && isNotSource;
          }
        );
        
        if (targetSection) {
          // Always use section number, not section name
          // Calculate index from sorted sections (this is the correct index for display)
          const targetIndex = sortedSections.findIndex(
            s => s.tree_node_id === targetSection.tree_node_id || 
                 s.section_id_ref === targetSection.section_id_ref
          );
          if (targetIndex !== -1) {
            targetSectionTitle = `Section ${targetIndex + 1}`;
          }
        }
      }
      
      // Fallback: use provided targetIndex if target section not found
      // But verify the section at that index matches targetId and is not source
      if (!targetSectionTitle && movePosition.targetIndex !== undefined && movePosition.targetIndex !== null && sortedSections.length > 0) {
        if (sortedSections[movePosition.targetIndex]) {
          const sectionAtIndex = sortedSections[movePosition.targetIndex];
          const isTarget = (sectionAtIndex.tree_node_id === movePosition.targetId || 
                           sectionAtIndex.section_id_ref === movePosition.targetId);
          const isNotSource = sectionAtIndex.tree_node_id !== sourceSectionId && 
                             sectionAtIndex.section_id_ref !== sourceSectionId;
          
          if (isTarget && isNotSource) {
            // Always use section number, not section name
            targetSectionTitle = `Section ${movePosition.targetIndex + 1}`;
          }
        }
      }
      
      // Final fallback: use provided index if nothing else worked
      if (!targetSectionTitle && movePosition.targetIndex !== undefined && movePosition.targetIndex !== null) {
        targetSectionTitle = `Section ${movePosition.targetIndex + 1}`;
      }

      if (!targetSectionTitle) {
        targetSectionTitle = 'Target Section';
      }

      const positionText = position === 'above' ? 'Above' : 'Below';
      return `Confirm ${operation} of ${sectionTitle} ${positionText} ${targetSectionTitle}`;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content_addsection" style={{ position: 'relative' }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <h4
            style={{
              marginLeft: "23px",
              fontSize: "18px",
              color: "#333333",
            }}
          >
            {getConfirmationMessage()}
          </h4>
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#666",
            }}
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{ padding: "24px", marginTop: "-28px", color: "#333333" }}
        >
          <h4 style={{ fontSize: "14px" }}>
            {moveNode
              ? `For ${moveDocument.type} ${moveDocument.partNumber} > Rev ${moveDocument.revision}`
              : `For ${moveDocument.type} ${targetDocument.partNumber} > Rev ${targetDocument.revision}`
            }
          </h4>

          <p style={{ fontSize: "14px", color: "#333333" }}>
            <strong>Editor:</strong>{" "}
            <span style={{ marginLeft: "10px" }}>
              {localStorage.getItem("user_name")}
            </span>
          </p>

          <label
            className="modal-label"
            style={{ fontSize: "14px", color: "#333333" }}
          >
            Comment
            <span className="required" style={{ marginLeft: "-1px" }}>
              *
            </span>
          </label>
          <textarea
            className="modal-textarea"
            placeholder="Enter your comments here..."
            value={comment || ''}
            onChange={(e) => onCommentChange(e.target.value)}
            style={{
              width: "100%",
              minHeight: "100px",
              padding: "8px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
            disabled={isLoading}
          ></textarea>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "16px 24px",
            borderTop: "1px solid #e0e0e0",
            marginTop: "-10px",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#6c757d",
              color: "#e9ecef",
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
            disabled={isLoading}
          >
            Close
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "8px 16px",
              border: "1px solid #007bff",
              borderRadius: "4px",
              background: "#007bff",
              color: "white",
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.7 : 1,
            }}
            disabled={isLoading}
          >
            Save
          </button>
        </div>
      </div>

      {/* Loader overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            borderRadius: '8px',
          }}
        >
          <Loader text="Processing..." />
        </div>
      )}
    </div>
  );
};

export default MoveReplaceConfirmModal;

